import { Button, H1, Paragraph, Textarea } from '@flysas-tech/react-components';

import { ContentOctopus } from '#assets/content-octopus.jsx';
import { SadOctopus } from '#assets/sad-octopus.jsx';
import { SurprisedOctopus } from '#assets/surprised-octopus.jsx';

const octopusPosition = 'absolute bottom-[-150px] left-[-250px]';

/**
 * Creates a layout component.
 * @param {object} properties - The component props object.
 * @param {import('react').ReactNode} properties.children - Any React.Node element that will be placed in the layout.
 * @returns {import('react').ReactElement} The layout component.
 */
function Layout({ children }) {
  return (
    <article className="flex h-full m-auto items-center justify-center">
      <div className="flex flex-col relative">{children}</div>
    </article>
  );
}

/**
 * Creates a chat bubble.
 * @param {object} properties - The component props object.
 * @param {string} properties.message - The component message value.
 * @param {string} properties.title - The component title value.
 * @param {import('react').ReactNode} [properties.children] - Any React.Node element that will be placed last in the chat bubble.
 * @returns {import('react').ReactElement<{ message: string, title: string }>} The chat bubble.
 */
function ChatBubble({
  children,
  message = "It seems that something went wrong, I apologize for the inconvenience. Please send my team a message about the struggle or just try again later. No need to worry, we're working on resolving it!",
  title = 'Uh oh',
}) {
  return (
    <>
      <div className="flex flex-col bg-white p-8 rounded-t-2xl rounded-r-2xl max-w-2xl ">
        <H1 hero className="pb-2 text-grey-50">
          {title}
        </H1>
        <Paragraph variant="introduction" className=" text-grey-50">
          {message}
        </Paragraph>
        <div className="pt-6">{children}</div>
      </div>
      <hr className=" h-0 w-0 border-r-[3rem] border-t-[3rem] border-white border-r-transparent" />
    </>
  );
}

/**
 * Creates a 404 error page.
 * @returns {import('react').ReactElement} The error page.
 */
export function Error404() {
  return (
    <Layout>
      <SurprisedOctopus className={octopusPosition} />
      <ChatBubble
        title="Oops"
        message="The page you're looking for seems to have vanished into thin air. Double-check the URL or navigate back to safety."
      >
        <Button variant="secondary" size="large" as="a" href="/">
          Go back
        </Button>
      </ChatBubble>
    </Layout>
  );
}

/**
 * Creates a 401 error page.
 * @param {object} properties - The component props object.
 * @param {string} properties.message - The primary message.
 * @param {string} properties.title - The primary title.
 * @returns {import('react').ReactElement<{ message: string, title: string }>} The 401 error page.
 */
export function Error401({
  message = 'You do not currently have permission to access this page. However, you can request access from Service Now.',
  title = 'Hmm!',
}) {
  return (
    <Layout>
      <SurprisedOctopus className={octopusPosition} />
      <ChatBubble title={title} message={message}>
        <Button variant="secondary" size="large" as="a" href="/">
          Go back
        </Button>
      </ChatBubble>
    </Layout>
  );
}

/**
 * Creates a 500 error page.
 * @param {object} properties - The component props object.
 * @param {string} properties.message - The primary message.
 * @param {string} properties.title - The primary title.
 * @returns {import('react').ReactElement<{ message: string, title: string }>} The 500 error page.
 */
export function Error500({
  message = 'Something went wrong on our end, I apologize for the inconvenience. Please send my team a message about the struggle or just try again later. No need to worry, we are working on resolving it!',
  title,
}) {
  return (
    <Layout>
      <SadOctopus className={octopusPosition} />
      <ChatBubble title={title} message={message}>
        <Textarea name="message" placeholder="What were you trying to do? / When did it go wrong?" />
        <Button className="mt-4" variant="secondary" size="large">
          Report
        </Button>
      </ChatBubble>
    </Layout>
  );
}

/**
 * Creates an error routing error page.
 * @param {object} properties - The component props object.
 * @param {string} properties.message - The primary message.
 * @param {string} properties.title - The primary title.
 * @returns {import('react').ReactElement<{ message: string, title: string }>} The routing error page.
 */
export function ErrorRouting({
  message = 'It seems that something went wrong with the routing. Please send my team a message about the struggle or just try again later. No need to worry, we are working on resolving it!',
  title,
}) {
  return (
    <Layout>
      <SadOctopus className={octopusPosition} />
      <ChatBubble title={title} message={message}>
        <Textarea name="message" placeholder="What were you trying to do? / When did it go wrong?" />
        <Button className="mt-4" variant="secondary" size="large">
          Report
        </Button>
      </ChatBubble>
    </Layout>
  );
}

/**
 * Creates a default error page.
 * @param {object} properties - The component props object.
 * @param {string} properties.message - The primary message.
 * @param {string} properties.title - The primary title.
 * @returns {import('react').ReactElement<{ message: string, title: string }>} The default error page.
 */
export function ErrorDefault({ message, title }) {
  return (
    <Layout>
      <SadOctopus className={octopusPosition} />
      <ChatBubble title={title} message={message}>
        <div>
          <Textarea name="message" placeholder="What were you trying to do? / When did it go wrong?" />
          <Button className="mt-4" variant="secondary" size="large">
            Report
          </Button>
        </div>
      </ChatBubble>
    </Layout>
  );
}

/**
 * Creates a default success page.
 * @param {object} properties - The component props object.
 * @param {string} properties.message - The primary message.
 * @param {string} properties.title - The primary title.
 * @param {import('react').ReactNode} properties.children - Any React.Node element that will be placed last in the chat bubble.
 * @returns {import('react').ReactElement<{ message: string, title: string }>} The default success page.
 */
export function SuccessPage({
  children,
  message = 'We will look into it and get back to you as soon as possible.',
  title = 'Thank you!',
}) {
  return (
    <Layout>
      <ContentOctopus className={octopusPosition} />
      <ChatBubble title={title} message={message}>
        {children}
      </ChatBubble>
    </Layout>
  );
}

/**
 * Creates a default success page.
 * @param {object} properties - The component props object.
 * @param {string} properties.message - The primary message.
 * @param {string} properties.title - The primary title.
 * @param {import('react').ReactNode} properties.children - Any React.Node element that will be placed last in the chat bubble.
 * @returns {import('react').ReactElement<{ message: string, title: string }>} The default success page.
 */
export function NothingWentRightPage({
  children,
  message = 'Nothing seems to be going my way today, I apologize for the inconvenience. Please try again later.',
  title = 'Uh oh!',
}) {
  return (
    <Layout>
      <SadOctopus className={octopusPosition} />
      <ChatBubble title={title} message={message}>
        {children}
      </ChatBubble>
    </Layout>
  );
}
